import {
  cn,
  Button,
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@wellesley/ui'
import { forwardRef, PropsWithChildren, FC, useState } from 'react'
import UploadButton from '@wellesley/uploads/components/UploadButton'
import { ImagePlus, Trash2 } from 'lucide-react'

export type ChangeImgButtonProps = PropsWithChildren<{
  accept: string
  imgType: ImgType
  onChange: (props: UploadFileProps[]) => void
  onDelete?: () => void
  disableDelete?: boolean
  className?: string
  disabled?: boolean
  hasPreview?: boolean
  isButtonWrapper?: boolean
}>

export type UploadFileProps = {
  file: File
  objectUrl: string
}

type ButtonWrapperProps = {
  children: React.ReactNode
  isWrapper?: boolean
  disabled?: boolean
  isLabel?: boolean
}

type ImgType = 'cover' | 'photo'

const ChangeImgButton = forwardRef<HTMLInputElement, ChangeImgButtonProps>(
  (
    {
      accept,
      children,
      className,
      hasPreview,
      onChange,
      onDelete,
      disabled,
      imgType,
      disableDelete,
      isButtonWrapper = true,
    },
    ref,
  ) => {
    const dataId = imgType === 'cover' ? 'changeCover' : 'changeAvatar'

    const [isOpen, setIsOpen] = useState(false)
    const handleOpenChange = (open: boolean) => {
      setIsOpen(open)
    }

    const handleChange = (props: UploadFileProps[]) => {
      onChange(props)
      setIsOpen(false)
    }

    return (
      <div
        data-testid={dataId}
        className={cn(
          'my-xxs gap-xxs flex cursor-pointer items-center md:my-0',
          className,
        )}
      >
        {hasPreview ? (
          <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger>
              <ButtonWrapper disabled={disabled} isWrapper={isButtonWrapper}>
                {children || `Edit ${imgType}`}
              </ButtonWrapper>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={imgType === 'cover' ? 'end' : 'start'}
              className={cn({
                'mt-xxxs': imgType === 'photo',
              })}
            >
              <label className="block w-full items-center">
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                  }}
                  className="cursor-pointer"
                >
                  <ImagePlus size={20} /> Upload {imgType}
                  <UploadButton
                    ref={ref}
                    accept={accept}
                    onChange={handleChange}
                  />
                </DropdownMenuItem>
              </label>
              <DropdownMenuItem
                onClick={() => {
                  onDelete?.()
                }}
                disabled={disableDelete}
                className="cursor-pointer text-red-500"
              >
                <Trash2 size={20} />
                Remove {imgType}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <ButtonWrapper
            isLabel={true}
            disabled={disabled}
            isWrapper={isButtonWrapper}
          >
            {children || `Add ${imgType}`}
            <UploadButton ref={ref} accept={accept} onChange={handleChange} />
          </ButtonWrapper>
        )}
      </div>
    )
  },
)

export const ButtonWrapper: FC<ButtonWrapperProps> = ({
  children,
  disabled,
  isLabel,
  isWrapper,
}) => {
  if (!isWrapper) {
    return <label>{children}</label>
  }

  const buttonProps = {
    variant: 'secondary' as const,
    size: 's' as const,
    ...(disabled
      ? { disabled: true }
      : { as: isLabel ? ('label' as const) : ('button' as const) }),
  }

  return (
    <Button {...buttonProps} className="cursor-pointer">
      {children}
    </Button>
  )
}

ChangeImgButton.displayName = 'ChangeImgButton'

export default ChangeImgButton
